
import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel, FormGroup, FormHelperText } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePermission } from '../hooks/usePermission';
import config from '../config/config';
import { useToast } from '../context/ToastContext';

const allPermissions = [
  'product:read', 'product:create', 'product:update', 'product:delete',
  'order:read', 'order:create', 'order:update',
  'customer:read', 'customer:create', 'customer:update', 'customer:delete',
  'user:manage'
];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [form, setForm] = useState({
    role: '',
    permissions: [],
  });
  const [validationErrors, setValidationErrors] = useState({});
  const { token, user: loggedInUser } = useAuth();
  const { hasPermission } = usePermission();
  const { showToast } = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${config.backendUrl}/api/users`, {
        headers: { 'x-auth-token': token }
      });
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleClickOpen = (user = null) => {
    setCurrentUser(user);
    if (user) {
      setForm({
        role: user.role,
        permissions: user.permissions || [],
      });
    } else {
      setForm({
        role: 'staff',
        permissions: [],
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentUser(null);
    setForm({
      role: 'staff',
      permissions: [],
    });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePermissionChange = (e) => {
    const { value, checked } = e.target;
    setForm(prevForm => ({
      ...prevForm,
      permissions: checked
        ? [...prevForm.permissions, value]
        : prevForm.permissions.filter(p => p !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    if (!form.role) {
      errors.role = 'Vai trò là bắt buộc';
    }
    if (form.role === 'staff' && form.permissions.length === 0) {
      errors.permissions = 'Nhân viên phải có ít nhất một quyền hạn';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      await axios.put(`${config.backendUrl}/api/users/${currentUser._id}`, form, {
        headers: { 'x-auth-token': token }
      });
      fetchUsers();
      handleClose();
      setValidationErrors({}); // Clear errors on success
      showToast('Người dùng đã được cập nhật thành công!', 'success');
    } catch (err) {
      console.error(err);
      let errorMessage = 'Đã xảy ra lỗi khi cập nhật người dùng.';
      if (err.response && err.response.data) {
        if (err.response.data.errors) {
          const apiErrors = {};
          err.response.data.errors.forEach(error => {
            apiErrors[error.param] = error.msg;
          });
          setValidationErrors(apiErrors);
          errorMessage = err.response.data.errors[0].msg || errorMessage;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }
      showToast(errorMessage, 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${config.backendUrl}/api/users/${id}`, {
        headers: { 'x-auth-token': token }
      });
      fetchUsers();
      showToast('Người dùng đã được xóa thành công!', 'success');
    } catch (err) {
      console.error(err);
      let errorMessage = 'Đã xảy ra lỗi khi xóa người dùng.';
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      }
      showToast(errorMessage, 'error');
    }
  };

  return (
    <Container>
      <Box sx={{ mt: 8 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Quản lý Người dùng
        </Typography>

        <TableContainer component={Paper} sx={{ mt: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tên</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Vai trò</TableCell>
                <TableCell>Quyền hạn</TableCell>
                <TableCell>Hành động</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.permissions.join(', ')}</TableCell>
                  <TableCell>
                    {hasPermission(['user:manage']) && loggedInUser.id !== user._id && (
                      <IconButton onClick={() => handleClickOpen(user)} color="primary">
                        <EditIcon />
                      </IconButton>
                    )}
                    {hasPermission(['user:manage']) && loggedInUser.id !== user._id && (
                      <IconButton onClick={() => handleDelete(user._id)} color="secondary">
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={open} onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
    }}>
          <DialogTitle>Chỉnh sửa Người dùng</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                fullWidth
                label="Tên Người dùng"
                name="name"
                value={currentUser?.name || ''}
                disabled
              />
              <TextField
                margin="normal"
                fullWidth
                label="Email"
                name="email"
                value={currentUser?.email || ''}
                disabled
              />
              <FormControl fullWidth margin="normal" error={!!validationErrors.role}>
                <InputLabel>Vai trò</InputLabel>
                <Select
                  name="role"
                  value={form.role}
                  label="Role"
                  onChange={handleChange}
                >
                  <MenuItem value="admin">Quản trị viên</MenuItem>
                  <MenuItem value="staff">Nhân viên</MenuItem>
                </Select>
                {!!validationErrors.role && <FormHelperText>{validationErrors.role}</FormHelperText>}
              </FormControl>

              {form.role === 'staff' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6">Quyền hạn</Typography>
                  <FormGroup>
                    {allPermissions.map(permission => (
                      <FormControlLabel
                        key={permission}
                        control={
                          <Checkbox
                            value={permission}
                            checked={form.permissions.includes(permission)}
                            onChange={handlePermissionChange}
                          />
                        }
                        label={
                          permission === 'product:read' ? 'Sản phẩm: đọc' :
                          permission === 'product:create' ? 'Sản phẩm: tạo' :
                          permission === 'product:update' ? 'Sản phẩm: cập nhật' :
                          permission === 'product:delete' ? 'Sản phẩm: xóa' :
                          permission === 'order:read' ? 'Đơn hàng: đọc' :
                          permission === 'order:create' ? 'Đơn hàng: tạo' :
                          permission === 'order:update' ? 'Đơn hàng: cập nhật' :
                          permission === 'customer:read' ? 'Khách hàng: đọc' :
                          permission === 'customer:create' ? 'Khách hàng: tạo' :
                          permission === 'customer:update' ? 'Khách hàng: cập nhật' :
                          permission === 'customer:delete' ? 'Khách hàng: xóa' :
                          permission === 'user:manage' ? 'Người dùng: quản lý' :
                          permission
                        }
                      />
                    ))}
                  </FormGroup>
                  {!!validationErrors.permissions && <Typography color="error" variant="caption">{validationErrors.permissions}</Typography>}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Hủy</Button>
            <Button onClick={handleSubmit}>Cập nhật</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default UserManagement;
